import React, { useState, useRef } from "react";
import { Radio, Select, Space } from "antd";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

// graphql
import GET_COURSES from "graphql/queries/GetCourses";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_COURSE_INFO from "graphql/queries/GetCourseInfo";
import useBannerBackground from "hooks/useBannerBackground";
import GET_LANGUAGES from "graphql/queries/GetLanguages";

// custom types
import { Level, Course, Language } from "types";

// import types for antd components
import { SelectValue } from "antd/lib/select";
import { RadioChangeEvent } from "antd/lib/radio";
import screenSize from "utils/screenSize";

// styling
import * as Layout from "common/layout";
import {
  BannerSection,
  CourseBannerTitle,
  CourseTypeFilter,
  LevelSelectorContainer,
  SelectorLabel,
  LanguageSelector,
  LevelSelector,
  CourseSelector,
  TimeInfoRow,
  TimeInfo,
} from "./BookACourseStyle";

const { SectionHeader, HeaderCol } = Layout;
const { Option } = Select;

type CourseBannerProps = {
  currentCourseId?: number;
  setCurrentCourseId: (id?: number) => void;
};

type CourseFilter = {
  levelId?: number;
  languageId?: string;
  courseType?: string;
  courseDays?: string;
};

const CourseBanner = ({
  currentCourseId,
  setCurrentCourseId,
}: CourseBannerProps) => {
  const { t } = useTranslation("book_a_course");
  const [filter, setFilter] = useState<CourseFilter>({
    levelId: undefined,
    languageId: undefined,
    courseType: "standard",
    courseDays: "weekdays",
  });
  const { data: languagesData } = useQuery(GET_LANGUAGES);

  const courseSelectorRef = useRef<HTMLSelectElement>(null);
  const focusCourseSelector = () => {
    return courseSelectorRef.current && courseSelectorRef.current.focus();
  };

  const changeFilter = (newFilter: CourseFilter) => {
    setFilter({
      ...filter,
      ...newFilter,
    });
  };

  const onLanguageFieldChange = (value: string) => {
    const studyLanguage = languagesData.languages.find((language: Language) => {
      return language.id === value;
    });

    changeFilter({
      languageId: studyLanguage.id,
      levelId: studyLanguage.levels[0]?.id,
    });
    focusCourseSelector();
  };

  const manuallyChangeFilter = (newFilter: CourseFilter) => {
    changeFilter(newFilter);
    focusCourseSelector();
  };

  const { data: courseList, loading: loadingCourseList } = useQuery(
    GET_COURSES,
    {
      skip: !filter.levelId || !filter.courseType,
      variables: {
        ...filter,
        // Backend does not support this field
        languageId: undefined,
      },
      fetchPolicy: "network-only",
    }
  );

  const { bannerBackgroundUrl } = useBannerBackground();

  // Set default level equal to user's current level
  const { loading: loadingUserInfo } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
    onCompleted: ({ me: { currentLevel, currentLanguage } }) => {
      if (!filter.levelId && !filter.languageId) {
        changeFilter({
          languageId: currentLanguage.id,
          levelId: currentLevel.id,
        });
      }
    },
  });

  // Set default filter accourding to selected course
  useQuery(GET_COURSE_INFO, {
    skip: !currentCourseId,
    variables: {
      id: currentCourseId,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data) {
        const { courseType, levelId, courseDays } = data.courseDetails;
        changeFilter({
          courseType,
          levelId,
          courseDays,
          languageId: undefined,
        });
      }
    },
  });

  if (
    currentCourseId &&
    languagesData &&
    filter.levelId &&
    !filter.languageId
  ) {
    const studyLanguage = languagesData.languages.find((language: Language) => {
      return language.levels.some(
        (level: { id: number }) => level.id === filter.levelId
      );
    });
    changeFilter({
      languageId: studyLanguage.id,
    });
  }

  const studyLanguage = languagesData?.languages.find(
    (language: { id: string }) => {
      return language.id === filter.languageId;
    }
  );

  return (
    <BannerSection background-url={bannerBackgroundUrl}>
      <SectionHeader>
        <HeaderCol lg={2} md={24}>
          <CourseBannerTitle>{t("COURSE")}</CourseBannerTitle>
        </HeaderCol>
        <HeaderCol lg={22} md={24}>
          <Space
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              rowGap: screenSize() === "mobile" ? "0px" : "12px",
            }}
            direction={screenSize() === "mobile" ? "vertical" : "horizontal"}
            align="baseline"
            size={screenSize() === "mobile" ? "middle" : "small"}
          >
            <CourseTypeFilter
              value={filter.courseType}
              onChange={(ev: RadioChangeEvent) => {
                manuallyChangeFilter({
                  courseType: ev.target.value as string,
                });
              }}
              style={{ marginRight: "12px" }}
            >
              <Radio.Button value="standard">
                {t("Standard 4h/wk")}
              </Radio.Button>
              <Radio.Button value="intensive">
                {t("Intensive 10h/wk")}
              </Radio.Button>
            </CourseTypeFilter>

            {screenSize() === "mobile" && (
              <div style={{ flexBasis: "100%", height: 0 }} />
            )}

            <CourseTypeFilter
              value={filter.courseDays}
              onChange={(ev: RadioChangeEvent) =>
                manuallyChangeFilter({
                  courseDays: ev.target.value as string,
                })
              }
            >
              <Radio.Button value="weekdays">{t("Weekdays")}</Radio.Button>
              <Radio.Button value="weekends">{t("Weekends")}</Radio.Button>
            </CourseTypeFilter>

            {screenSize() === "mobile" && (
              <div style={{ flexBasis: "100%", height: 0 }} />
            )}

            <LevelSelectorContainer>
              <SelectorLabel>{t("Language")}</SelectorLabel>
              <LanguageSelector
                placeholder={t("Language")}
                value={filter.languageId}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value: SelectValue) => {
                  onLanguageFieldChange(value as string);
                }}
                dropdownStyle={{ textAlign: "left" }}
                loading={loadingUserInfo}
              >
                {languagesData?.languages.map((languages: Language) => (
                  <Option value={languages.id} key={languages.id}>
                    {`${languages.englishName}`}
                  </Option>
                ))}
              </LanguageSelector>
            </LevelSelectorContainer>

            <LevelSelectorContainer>
              <SelectorLabel>{t("Level")}</SelectorLabel>
              <LevelSelector
                placeholder={t("Level")}
                value={filter.levelId}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value: SelectValue) =>
                  manuallyChangeFilter({
                    levelId: value as number,
                  })
                }
                dropdownStyle={{ textAlign: "left" }}
                loading={loadingUserInfo}
              >
                {studyLanguage?.levels.map((level: Level) => (
                  <Option value={level.id} key={level.id}>
                    {level.title}
                  </Option>
                ))}
              </LevelSelector>
            </LevelSelectorContainer>
          </Space>
        </HeaderCol>
      </SectionHeader>

      <CourseSelector
        // showSearch
        placeholder={t(`Click Here To Select A {{language}} Course`, {
          language: studyLanguage?.englishName,
        })}
        optionFilterProp="children"
        getPopupContainer={(trigger) => trigger.parentNode}
        dropdownStyle={{ textAlign: "left" }}
        defaultValue={currentCourseId}
        onChange={(value: SelectValue) => setCurrentCourseId(value as number)}
        loading={loadingCourseList}
        ref={courseSelectorRef}
        showAction={["focus", "click"]}
        virtual={courseList?.courses.length > 7}
      >
        {courseList?.courses.map((course: Course) => (
          <Option value={course.id} key={course.id}>
            {course.description}
          </Option>
        ))}
      </CourseSelector>

      <TimeInfoRow gutter={36}>
        <TimeInfo>
          {t(
            'All course times are displayed in your chosen time zone (selected under "My Settings").'
          )}
        </TimeInfo>
      </TimeInfoRow>
    </BannerSection>
  );
};

export default CourseBanner;
